import React from "react";
import { Send, ArrowLeft, Paperclip, Eye, ShieldCheck, Calendar, Clock } from "lucide-react";

const PREVIEW_OUTCOME_LABELS = {
  advice_provided: "Advice Provided",
  matter_resolved: "Matter Resolved",
  follow_up_recommended: "Follow-up Consultation Recommended",
  representation_required: "Representation May Be Required",
  documentation_required: "Documentation Required",
  further_evidence_required: "Further Evidence Required",
};
const PREVIEW_FU_LABELS = { "7d": "7 days", "15d": "15 days", "30d": "30 days", custom: "a custom date" };

export function PreviewModal({ preview, attachments, onBack, onPublish, publishing }) {
  const info = preview.consultation_info || {};
  const fu = preview.follow_up || {};
  const atts = attachments?.length ? attachments : (preview.attachments || []);
  const Sec = ({ title, children }) => (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">{title}</h3>
      <div className="mt-1">{children}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm overflow-y-auto" data-testid="lcr-preview-modal">
      <div className="max-w-2xl mx-auto my-6 px-4">
        <div className="rounded-2xl bg-[#FBF4DF] border border-[#D4AF37]/40 px-4 py-3 flex items-center gap-2 mb-3">
          <Eye size={16} className="text-[#8a6d14] shrink-0"/>
          <p className="text-xs font-bold text-[#8a6d14]">
            Preview — this is exactly what your client will see. Internal notes are excluded.
          </p>
        </div>
        <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
          <div className="p-6 sm:p-8 bg-gradient-to-br from-[#0A2540] to-[#173a63] text-white">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Legal Consultation Record</p>
            <h1 className="mt-2 text-2xl font-extrabold" style={{ fontFamily: "Poppins" }} data-testid="preview-matter-title">
              {preview.matter_title || "Consultation"}
            </h1>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12}/> {info.lawyer_name || "—"}</span>
              <span className="inline-flex items-center gap-1"><Calendar size={12}/> {info.date || "—"}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12}/> {info.duration_minutes || 30} min</span>
              <span className="text-[#D4AF37] font-bold">Version v{(preview.version || 0) + 1}</span>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-5">
            {preview.client_issue && (
              <Sec title="Issue Discussed"><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{preview.client_issue}</p></Sec>
            )}
            {(preview.facts || []).length > 0 && (
              <Sec title="Facts Shared">
                <ul className="space-y-1">
                  {preview.facts.map((f, i) => (
                    <li key={`${f}-${i}`} className="text-sm text-slate-700 pl-4 relative">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#0A2540]"/>{f}
                    </li>
                  ))}
                </ul>
              </Sec>
            )}
            {(preview.documents_reviewed || []).length > 0 && (
              <Sec title="Documents Reviewed">
                <ul className="space-y-1">
                  {preview.documents_reviewed.map((d, i) => (
                    <li key={`${d}-${i}`} className="text-sm text-slate-700 pl-4 relative">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#0A2540]"/>{d.title}
                    </li>
                  ))}
                </ul>
              </Sec>
            )}
            {preview.legal_opinion && (
              <Sec title="Legal Opinion"><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{preview.legal_opinion}</p></Sec>
            )}
            {(preview.recommended_steps || []).length > 0 && (
              <Sec title="Recommended Next Steps">
                <ul className="space-y-1">
                  {preview.recommended_steps.map((s, i) => (
                    <li key={`${s}-${i}`} className="text-sm text-slate-700 pl-4 relative">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#0A2540]"/>{s}
                    </li>
                  ))}
                </ul>
              </Sec>
            )}
            {fu.recommended && (
              <Sec title="Follow-up Consultation">
                <p className="text-sm text-slate-700">
                  Recommended after <b>{PREVIEW_FU_LABELS[fu.after] || fu.after || "—"}</b>
                  {fu.after === "custom" && fu.after_date ? ` (${fu.after_date})` : ""}
                  {fu.note ? ` — ${fu.note}` : ""}
                </p>
              </Sec>
            )}
            {(preview.outcome || []).length > 0 && (
              <Sec title="Consultation Outcome">
                <div className="flex flex-wrap gap-2 mt-1">
                  {preview.outcome.map((o) => (
                    <span key={o} className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      {PREVIEW_OUTCOME_LABELS[o] || o}
                    </span>
                  ))}
                </div>
              </Sec>
            )}
            {atts.length > 0 && (
              <Sec title="Attachments">
                <ul className="space-y-1 mt-1">
                  {atts.map((a) => (
                    <li key={a.id} className="text-sm bg-slate-50 rounded-xl px-3 py-2 flex items-center gap-2">
                      <Paperclip size={14} className="text-slate-500"/> <span>{a.name}</span>
                    </li>
                  ))}
                </ul>
              </Sec>
            )}
          </div>
        </div>
        <div className="sticky bottom-4 mt-4 flex flex-wrap justify-end gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-lg">
          <p className="flex-1 text-xs text-slate-500 self-center min-w-[180px]">
            Publishing makes this version visible to your client and notifies them.
          </p>
          <button
            onClick={onBack}
            className="px-4 py-2 text-xs font-bold rounded-full border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
            data-testid="preview-back"
          >
            <ArrowLeft size={13}/> Back to editing
          </button>
          <button
            onClick={onPublish}
            disabled={publishing}
            className="px-5 py-2 text-xs font-bold rounded-full bg-[#0A2540] text-[#D4AF37] hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1"
            data-testid="preview-confirm-publish"
          >
            <Send size={13}/> {publishing ? "Publishing…" : "Confirm & Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
